import Slider from "components/DesignSystem/Slider/Slider";
import { ReactNode, useState } from "react";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import Async from "components/DesignSystem/AsyncComponents/Async";
import classNames from "classnames";
import {
  Registration,
  useGetSalesTaxRegistrationDetailsQuery,
} from "store/apis/salesTaxRegistration";
import { Accordion } from "components/DesignSystem/Accordion/Accordion";
import dayjs from "dayjs";
import { DISPLAY_DATE } from "constants/date";
import { FilePdf } from "components/icons/FilePdf";
import { DownloadIcon } from "components/icons/Download";
import { paymentScheduleMap } from "./RegistrationDetails";
import {
  FILING_IN_PROGRESS,
  FILING_INFO_MISSING,
  IN_REVIEW,
  NOT_STARTED,
  PENDING_DETAILS,
  PROCESSING,
  REGISTERED,
  VALIDATING,
} from "constants/salesTaxRegistrationStatusType";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { Preview } from "components/PreviewModal";
import { RegistrationStatusCard } from "./RegistrationStatusCard";
import { Button } from "components/DesignSystem/Button/Button";
import ToolTip from "components/design/toolTip";
import { ToolTipIcon } from "components/icons/TooltipIcon";

const statusMap = {
  [REGISTERED]: "Registered",
  [PROCESSING]: "Processing",
  [PENDING_DETAILS]: "Not started",
  [IN_REVIEW]: "In review",
  [VALIDATING]: "In cart",
  [FILING_IN_PROGRESS]: "Filing in progress",
  [NOT_STARTED]: "Not started",
  [FILING_INFO_MISSING]: "Filing info missing",
};

const FileContainer = ({
  onFileClick,
  children,
}: {
  onFileClick: () => void;
  children: ReactNode;
}) => {
  return (
    <div
      onClick={onFileClick}
      className="t-flex t-items-center t-cursor-pointer t-justify-between t-border t-border-solid t-border-neutral-10 t-rounded-md t-p-2 "
    >
      <div className="t-flex t-items-center t-gap-x-2">
        <FilePdf />
        <p className="t-m-0 t-text-body-sm t-text-text-100 t-max-w-60 t-overflow-hidden t-whitespace-nowrap t-text-ellipsis">
          {children}
        </p>
      </div>
      <DownloadIcon />
    </div>
  );
};

const StatusBadge = ({
  children,
  status,
}: {
  children: ReactNode;
  status: string;
}) => {
  return (
    <div
      className={classNames(
        "t-flex t-items-center t-gap-2 t-max-w-fit t-text-text-60 t-py-1 t-text-body-sm t-px-2 t-rounded-md",
        {
          "t-bg-blue-10 ": status === PROCESSING,
          "t-bg-green-20": status === REGISTERED,
          "t-bg-blue-10":
            status === IN_REVIEW ||
            status === VALIDATING ||
            status === FILING_IN_PROGRESS,
          "t-bg-red-10": status === NOT_STARTED || status === PENDING_DETAILS,
          "!t-bg-neutral-0 !t-text-neutral-70": status === FILING_INFO_MISSING,
        }
      )}
    >
      <span className="t-min-w-fit t-text-body-sm  t-whitespace-nowrap">
        {children}
      </span>
    </div>
  );
};

export const SalesTaxRegisrationSlider = ({
  isOpen,
  onClose,
  selectedRegistrationId,
  handleClick,
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedRegistrationId: string;
  handleClick: (rowData: Registration) => void;
}) => {
  const entityId = useCurrentEntityId();
  const {
    data: registrationDetailsData,
    isLoading,
    isSuccess,
    isFetching,
  } = useGetSalesTaxRegistrationDetailsQuery(
    {
      entityId,
      state_registration_id: selectedRegistrationId,
    },
    { skip: !selectedRegistrationId, refetchOnMountOrArgChange: true }
  );
  const { uuid: groupId } = useCurrentGroupContext();
  const [previewFileId, setPreviewFileId] = useState<string | null>(null);

  const handleFileClick = ({ fileId }: { fileId: string }) => {
    setPreviewFileId(fileId);
  };

  const isStatusValidatingOrRegistered =
    registrationDetailsData &&
    Boolean(
      registrationDetailsData?.status === VALIDATING ||
        registrationDetailsData?.status === REGISTERED
    );

  const hasApplicationFile = Boolean(
    registrationDetailsData?.application?.file_id
  );
  const hasRegistrationLicenseFile = Boolean(
    registrationDetailsData?.registration_license?.file_id
  );

  return (
    <>
      <Slider.Root open={isOpen} onOpenChange={onClose}>
        <Slider.Content width={600}>
          <Async.Root
            isSuccess={isSuccess}
            isLoading={isLoading || isFetching}
            isEmpty={false}
          >
            <Async.Success>
              <DashboardContainer className="t-h-full t-relative">
                <DashboardContainer.Header>
                  <Slider.Header>
                    <Slider.Title className="t-gap-x-2 t-items-center t-flex">
                      <span className="t-text-text-100 t-text-subtitle-lg">
                        {" "}
                        {registrationDetailsData?.state}
                      </span>
                      <StatusBadge
                        status={registrationDetailsData?.status || ""}
                      >
                        {statusMap[registrationDetailsData?.status!]}
                      </StatusBadge>
                    </Slider.Title>
                    <Slider.Close />
                  </Slider.Header>
                </DashboardContainer.Header>
                <DashboardContainer.Content className="t-h-full t-relative t-top-0">
                  <Slider.Body>
                    <Accordion.Root
                      className="t-w-full t-space-y-4"
                      type="multiple"
                      defaultValue={["DETAILS", "DOCUMENTS"]}
                    >
                      {Boolean(
                        registrationDetailsData &&
                          registrationDetailsData.status !== NOT_STARTED &&
                          registrationDetailsData.status !== PENDING_DETAILS
                      ) && (
                        <Accordion.Item
                          value="DETAILS"
                          className="t-border t-border-neutral-10 t-rounded-lg t-w-full"
                        >
                          <Accordion.Trigger>
                            <p className="t-text-subtext t-text-text-100 t-m-0">
                              Registration details
                            </p>
                          </Accordion.Trigger>
                          <Accordion.Content>
                            <div className="t-py-4 t-pt-2 t-bg-surface">
                              <div className="t-flex t-flex-col t-gap-y-4">
                                {isStatusValidatingOrRegistered && (
                                  <div>
                                    <p className="t-text-body-sm t-text-text-30  t-mb-1">
                                      Sales Tax ID
                                    </p>
                                    <p className="t-text-subtext t-text-text-100 t-m-0">
                                      {registrationDetailsData?.sales_tax_id ||
                                        "-"}
                                    </p>
                                  </div>
                                )}

                                {isStatusValidatingOrRegistered && (
                                  <div>
                                    <p className="t-text-body-sm t-text-text-30 t-mb-1">
                                      Payment schedule
                                    </p>
                                    <p className="t-text-subtext t-text-text-100 t-m-0">
                                      {(registrationDetailsData?.payment_schedule &&
                                        paymentScheduleMap[
                                          registrationDetailsData
                                            .payment_schedule
                                        ]) ||
                                        "-"}
                                    </p>
                                  </div>
                                )}

                                <div>
                                  <p className="t-text-body-sm t-text-text-30 t-mb-1 t-flex t-gap-x-1">
                                    Filing service start date{" "}
                                    <ToolTip
                                      text={
                                        <span>
                                          Date since we began filing for
                                          <br />
                                          you (e.g., April 1 for Q2 filing)
                                        </span>
                                      }
                                    >
                                      <div className="-t-mt-0.5">
                                        <ToolTipIcon />
                                      </div>
                                    </ToolTip>
                                  </p>

                                  <p className="t-text-subtext t-text-text-100 t-m-0">
                                    {registrationDetailsData?.filing_start_date
                                      ? dayjs(
                                          registrationDetailsData?.filing_start_date
                                        ).format(DISPLAY_DATE)
                                      : "-"}
                                  </p>
                                </div>

                                <div>
                                  <p className="t-text-body-sm t-text-text-30 t-mb-1  t-flex t-gap-x-1">
                                    Existing Tax account IDs in this state
                                    <ToolTip
                                      text={
                                        <span>
                                          e.g. Franchise Tax ID, Withholding{" "}
                                          <br />
                                          Tax ID, SOS ID, Payroll Tax ID, etc.
                                        </span>
                                      }
                                    >
                                      <div className="-t-mt-0.5">
                                        <ToolTipIcon />
                                      </div>
                                    </ToolTip>
                                  </p>
                                  <p className="t-text-subtext t-text-text-100 t-m-0">
                                    {registrationDetailsData?.tax_detail_id ||
                                      "-"}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </Accordion.Content>
                        </Accordion.Item>
                      )}

                      {Boolean(
                        hasApplicationFile || hasRegistrationLicenseFile
                      ) && (
                        <Accordion.Item
                          value="DOCUMENTS"
                          className="t-border t-border-neutral-10 t-rounded-lg t-w-full"
                        >
                          <Accordion.Trigger>
                            <p className="t-text-subtext t-text-text-100 t-m-0">
                              Documents
                            </p>
                          </Accordion.Trigger>
                          <Accordion.Content>
                            <div className="t-py-4 t-pt-2 t-bg-surface">
                              <div className="t-flex t-flex-col t-gap-y-4">
                                {hasApplicationFile && (
                                  <div>
                                    <p className="t-text-body-sm t-text-text-30  t-mb-1">
                                      Registration application summary
                                    </p>
                                    <FileContainer
                                      onFileClick={() =>
                                        handleFileClick({
                                          fileId:
                                            registrationDetailsData?.application
                                              ?.file_id!,
                                        })
                                      }
                                    >
                                      {registrationDetailsData?.application
                                        ?.file_name || "-"}
                                    </FileContainer>
                                  </div>
                                )}

                                {hasRegistrationLicenseFile && (
                                  <div>
                                    <p className="t-text-body-sm t-text-text-30 t-mb-1">
                                      Sales Tax registration license
                                    </p>
                                    <FileContainer
                                      onFileClick={() =>
                                        handleFileClick({
                                          fileId:
                                            registrationDetailsData
                                              ?.registration_license?.file_id!,
                                        })
                                      }
                                    >
                                      {registrationDetailsData
                                        ?.registration_license?.file_name ||
                                        "-"}
                                    </FileContainer>
                                  </div>
                                )}
                              </div>
                            </div>
                          </Accordion.Content>
                        </Accordion.Item>
                      )}
                    </Accordion.Root>
                    {Boolean(
                      registrationDetailsData &&
                        registrationDetailsData.status !== VALIDATING &&
                        registrationDetailsData.status !== REGISTERED
                    ) && (
                      <RegistrationStatusCard
                        status={registrationDetailsData!.status}
                      />
                    )}
                  </Slider.Body>
                </DashboardContainer.Content>
              </DashboardContainer>
              {Boolean(
                registrationDetailsData?.status === PROCESSING ||
                  registrationDetailsData?.status === PENDING_DETAILS
              ) && (
                <Slider.Footer className="t-sticky t-bottom-0">
                  <div className=" t-flex t-flex- t-gap-x-2 t-justify-end ">
                    {Boolean(true) && (
                      <Button
                        size="small"
                        customType="primary"
                        onClick={() =>
                          handleClick(registrationDetailsData as Registration)
                        }
                      >
                        {registrationDetailsData?.status === PROCESSING
                          ? "Add"
                          : "Initiate"}
                      </Button>
                    )}
                  </div>
                </Slider.Footer>
              )}
            </Async.Success>
          </Async.Root>
        </Slider.Content>
      </Slider.Root>
      <Preview
        showModal={Boolean(previewFileId)}
        closeModal={() => setPreviewFileId(null)}
        groupId={groupId}
        previewId={previewFileId}
      />
    </>
  );
};
